<template>
  <LayoutSecondary>
    <div :style="style.container">
      <DialogCompleteSurveyPrimary
        @complete-survey="completeSurvey"
      />
      <ModalSurveyWrapUpPrimary />
      <ModalTenantUserSkillEditPrimary
        @create-skill="createTenantUserSkill"
        @delete-skill="deleteTenantUserSkill"
        @update-skill="updateTenantUserSkill"
        @switch-mode="newGuidedMode => { guidedMode = newGuidedMode }"
      />
      <router-view
        v-if="initialized"
        :key="$route.fullPath"
        :guidedMode="guidedMode"
        :survey="survey"
        :surveyCategorySkills="surveyCategorySkills"
        :surveyCategoryTenantUserSkills="surveyCategoryTenantUserSkills"
        :surveyState="surveyState"
        :tenantUser="tenantUser"
        :tenantUserSkills="tenantUserSkills"
        @create-survey-category-tenant-user-skill="createSurveyCategoryTenantUserSkill"
        @delete-survey-category-tenant-user-skill="deleteSurveyCategoryTenantUserSkill"
      />
    </div>
  </LayoutSecondary>
</template>

<script>
  import ApiSurveyCategorySkill from '@js/api/survey-category-skill'
  import ApiSurveyCategoryTenantUserSkill
    from '@js/api/survey-category-tenant-user-skill'
  import ApiTenantUserSkill from '@js/api/tenant-user-skill'
  import ApiTenantUserSurvey from '@js/api/tenant-user-survey'
  import ApiTenantUserSurveyState from '@js/api/tenant-user-survey-state'
  import DialogCompleteSurveyPrimary
    from '@survey/dialogs/dialog-complete-survey-primary'
  import LayoutSecondary from '@modules/v2/layout/layout-secondary'
  import ModalSurveyWrapUpPrimary from '@survey/modals/modal-survey-wrap-up-primary'
  import ModalTenantUserSkillEditPrimary
    from '@modals/modal-tenant-user-skill-edit-primary'
  import { DIALOG_NAMES, MODAL_WINDOW_NAMES } from '@js/constants'
  import { mapGetters, mapState } from 'vuex'

  export default {
    name: 'SurveyRootView',
    components: {
      DialogCompleteSurveyPrimary,
      LayoutSecondary,
      ModalSurveyWrapUpPrimary,
      ModalTenantUserSkillEditPrimary,
    },
    data: function () {
      return {
        initialized: false,
        guidedMode: true,
        survey: {},
        surveyCategorySkills: [],
        surveyCategoryTenantUserSkills: [],
        surveyCode: '',
        surveyState: {},
        tenantUserSkills: [],
        style: {
          container: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            width: '100%',
          },
        },
      };
    },
    computed: {
      ...mapGetters(
        'globalVariables',
        ['getSurveyStateCompleted', 'getSurveyStatePending',],
      ),
      ...mapGetters('tenant', ['isEnterprise']),
      ...mapState({
        tenant: state => state.tenant,
        tenantUser: state => state.tenantUser,
      }),
    },
    created: async function () {
      const surveyCode = this.$route.params.surveyCode;
      if (
        surveyCode === undefined ||
        surveyCode === null ||
        surveyCode.length === 0
      ) {
        this.$store.commit('notification/setWarning', 'Survey access denied');
        this.$router.push({
          name: 'private-home',
        });
      }
      this.surveyCode = surveyCode;

      await this.fetchTenantUserSkills();
    },
    mounted: async function () {
      await this.fetchSurvey();
      await this.fetchSurveyState();
      if (this.surveyState === null) {
        await this.createSurveyState(this.getSurveyStatePending);
      }

      await this.fetchSurveyCategorySkills();
      await this.fetchSurveyCategoryTenantUserSkills();
      this.initialized = await true;
    },
    methods: {
      completeSurvey: async function (survey) {
        try {
          const payload = await {
            stateType: this.getSurveyStateCompleted,
          };
          const api = await new ApiTenantUserSurveyState(
            this.tenantUser.id,
            survey.id,
          );
          const res = await api.put(payload);
          await this.$store.commit(
            'notification/setMessage',
            'Survey completion reported',
          );
          await this.$modal.hide(DIALOG_NAMES.COMPLETE_SURVEY_PRIMARY);
          await this.openModalToGuideUser();
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to complete survey',
          );
        }
      },
      createSurveyCategoryTenantUserSkill: async function (
        surveyId,
        surveyCategoryId,
        tenantUserSkillId,
        skillId,
      ) {
        const skillIdsInSurveyCategory = await this.surveyCategorySkills
          .filter(elem => elem.surveyCategory.id === surveyCategoryId)
          .map(elem => elem.skill.id);
        if (skillIdsInSurveyCategory.includes(skillId)) {
          return;
        }

        try {
          const payload = {
            tenantUserSkillId: tenantUserSkillId,
          };
          const api = await new ApiSurveyCategoryTenantUserSkill(
            this.tenantUser.id,
            surveyId,
            surveyCategoryId,
          );
          const res = await api.post(payload);
          await this.surveyCategoryTenantUserSkills.push(res.data);
          await this.$store.commit(
            'notification/setMessage',
            `Added ${res.data.tenantUserSkill.skill.name} as Others`,
          );
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to send request',
          );
        }
      },
      createSurveyState: async function (stateType) {
        try {
          const payload = await {
            stateType: stateType,
          };
          const api = await new ApiTenantUserSurveyState(
            this.tenantUser.id,
            this.survey.id,
          );
          const res = await api.post(payload);
          this.surveyState = await res.data;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to send state',
          );
          this.$router.push({
            name: 'private-home',
          });
        }
      },
      createTenantUserSkill: async function (form, payload) {
        let tenantUserSkillId = null;
        let skillId = null;

        try {
          const payload = await form.getPostPayload();
          const api = await new ApiTenantUserSkill(this.tenantUser.id);
          const res = await api.post(payload);
          await this.tenantUserSkills.push(res.data);
          await this.hideModalWindowToEditTenantUserSkill();
          await this.$store.commit(
            'notification/setMessage',
            `Added ${res.data.skill.name}`,
          );
          tenantUserSkillId = await res.data.id;
          skillId = await res.data.skill.id;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to create skill',
          );
        }

        if (
          tenantUserSkillId !== null &&
          payload?.surveyId !== undefined &&
          payload?.surveyId !== null &&
          payload?.surveyCategoryId !== undefined &&
          payload?.surveyCategoryId !== null
        ) {
          await this.createSurveyCategoryTenantUserSkill(
            payload.surveyId,
            payload.surveyCategoryId,
            tenantUserSkillId,
            skillId,
          );
        }
      },
      deleteSurveyCategoryTenantUserSkill: async function (
        surveyId,
        surveyCategoryId,
        tenantUserSkillId
      ) {
        try {
          const api = await new ApiSurveyCategoryTenantUserSkill(
            this.tenantUser.id,
            surveyId,
            surveyCategoryId,
            tenantUserSkillId,
          );
          const res = await api.delete();
          const index = await this.surveyCategoryTenantUserSkills
            .findIndex(elem => elem.id === res.data.id);
          if (index >= 0) {
            this.surveyCategoryTenantUserSkills.splice(index, 1);
            await this.$store.commit(
              'notification/setMessage',
              `Removed ${res.data.tenantUserSkill.skill.name}`,
            );
          }
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to delete category skill',
          );
        }
      },
      deleteTenantUserSkill: async function (form) {
        try {
          const tenantUserSkillId = await form.params.id;
          const api = await new ApiTenantUserSkill(
            this.tenantUser.id,
            tenantUserSkillId
          );
          const res = await api.delete();
          await this.removeTenantUserSkill(res.data.id);
          await this.hideModalWindowToEditTenantUserSkill();
          await this.$store.commit(
            'notification/setMessage',
            `Deleted ${res.data.skill.name}`,
          );
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to delete skill',
          );
        }
      },
      fetchSurvey: async function () {
        try {
          const queryParams = {
            page: 1,
            pageSize: 1,
          };
          const api = await new ApiTenantUserSurvey(
            this.tenantUser.id,
            this.surveyCode,
          );
          const res = await api.get(queryParams);
          this.survey = await res.data;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to fetch survey',
          );
          this.$router.push({
            name: 'private-home',
          });
        }
      },
      fetchSurveyCategorySkills: async function () {
        try {
          const api = await new ApiSurveyCategorySkill(this.survey.id);
          const res = await api.get();
          this.surveyCategorySkills = await res.data;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to fetch skills',
          );
          this.$router.push({
            name: 'private-home',
          });
        }
      },
      fetchSurveyCategoryTenantUserSkills: async function () {
        try {
          const api = await new ApiSurveyCategoryTenantUserSkill(
            this.tenantUser.id,
            this.survey.id,
          );
          const res = await api.get();
          this.surveyCategoryTenantUserSkills = await res.data;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to fetch skills data',
          );
        }
      },
      fetchSurveyState: async function () {
        try {
          const api = await new ApiTenantUserSurveyState(
            this.tenantUser.id,
            this.survey.id,
          );
          const res = await api.get();
          this.surveyState = await res.data;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to fetch state',
          );
          this.$router.push({
            name: 'private-home',
          });
        }
      },
      fetchTenantUserSkills: async function () {
        try {
          const api = await new ApiTenantUserSkill(this.tenantUser.id);
          const res = await api.get();
          this.tenantUserSkills = await res.data;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to load skills'
          );
          this.$router.push({
            name: 'private-home',
          });
        }
      },
      hideModalWindowToEditTenantUserSkill: function () {
        this.$modal.hide(MODAL_WINDOW_NAMES.CONFIGURE_TENANT_USER_SKILL_PRIMARY);
      },
      openModalToGuideUser: function () {
        const params = {
          survey: this.survey,
        }
        this.$modal.show(MODAL_WINDOW_NAMES.WRAP_UP_SURVEY_PRIMARY, params);
      },
      removeTenantUserSkill: function (tenantUserSkillId) {
        const index = this.tenantUserSkills.findIndex(
          elem => elem.id === tenantUserSkillId);
        if (index >= 0) {
          this.tenantUserSkills.splice(index, 1);
        }
      },
      updateLocalTenantUserSkills: function (tenantUserSkill) {
        const sourceId = tenantUserSkill.id;

        this.tenantUserSkills = this.tenantUserSkills
          .reduce((result, elem) => {
            if (elem.id === sourceId) {
              elem = tenantUserSkill;
            } else {
              const deletedFromTargets = (
                elem.targets.includes(sourceId) &&
                !tenantUserSkill.targets.includes(elem.id)
              );
              const addedToTargets = (
                !elem.targets.includes(sourceId) &&
                tenantUserSkill.targets.includes(elem.id)
              );
              if (deletedFromTargets) {
                const index = elem.targets.findIndex(elem => elem === sourceId);
                elem.targets.splice(index, 1);
              } else if (addedToTargets) {
                elem.targets.push(sourceId);
              }
            }

            result.push(elem);
            return result;
          }, []
        );
      },
      updateTenantUserSkill: async function (form) {
        try {
          const tenantUserSkillId = form.params.id;
          const payload = form.getPutPayload();
          const api = new ApiTenantUserSkill(
            this.tenantUser.id,
            tenantUserSkillId,
          );
          const res = await api.put(payload);
          await this.updateLocalTenantUserSkills(res.data);
          await this.hideModalWindowToEditTenantUserSkill();
          await this.$store.commit(
            'notification/setMessage',
            `Updated ${res.data.skill.name}`,
          );
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to update skill',
          );
        }
      },
    },
  };
</script>
